import React, { useState } from "react";
import { Bed, Users, Loader2 } from "lucide-react";
import { HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { AccommodationListing } from "@/types";
import LazyLoad from "@/components/trip-builder/image/LazyLoad";
import { motion, AnimatePresence } from "framer-motion";
import { formatImageUrl, formatPrice } from "@/lib/general";
// import useTripStore from "@/stores/tripBuilder/useTrip";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";

interface AccommodationCardProps {
  listing: AccommodationListing;
  handleAddToTrip: (listing: AccommodationListing) => void;
  handleModalOpenMainLodging: (viewLodging: AccommodationListing) => void;
  isAdmin: boolean;
}

const AccommodationCard: React.FC<AccommodationCardProps> = ({
  listing,
  handleAddToTrip,
  handleModalOpenMainLodging,
  isAdmin,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  // const { isParticipant, setPermissionDialogOpen } = useTripStore();
  const { addingLodging } = useAccommodationStore();

  const [isLiked, setIsLiked] = useState(false);

  const handleLike = async () => {
    setIsLiked(!isLiked);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 3000);
  };

  return (
    <div className="w-full rounded-md">
      <div className="relative w-full">
        <button
          onClick={() => handleModalOpenMainLodging(listing)}
          className="rounded-md h-48 w-full overflow-hidden relative"
        >
          <LazyLoad
            src={formatImageUrl(listing.image_01, "high")}
            lowResSrc={formatImageUrl(listing.image_01, "low")}
            alt={listing.listing_name}
            className="w-full h-full object-cover"
          />
        </button>

        {/* Price overlay */}
        <div className="absolute top-2 left-2 flex flex-row items-center space-x-1 bg-white/80 backdrop-blur-md rounded-md px-2 py-1">
          <p className="text-xs font-light text-neutral-900">
            {listing.price
              ? formatPrice(listing.currency, listing.price)
              : "Price on request"}
          </p>
        </div>

        {/* Like button */}
        <div className="group z-10 absolute right-3 top-2 flex items-center justify-center bg-stone-100 w-[27px] h-[27px] rounded-full">
          <button
            onClick={handleLike}
            className="group relative flex items-center justify-center w-full h-full"
          >
            <AnimatePresence>
              {isLiked && (
                <motion.i
                  key="filled-heart"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="text-[14px] text-red-600 absolute"
                >
                  <HeartIconSolid className="w-4 h-4" />
                </motion.i>
              )}
            </AnimatePresence>
            <motion.div
              animate={isLiked ? { scale: 0 } : { scale: 1 }}
              className={`${
                isLiked ? "text-transparent" : "text-gray-600"
              } transition-colors duration-300`}
            >
              <HeartIcon className="w-4 h-4" />
            </motion.div>
            {isAnimating && (
              <motion.div
                initial={{ scale: 0.8, opacity: 1 }}
                animate={{ scale: 1.3, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 bg-red-500/75 rounded-full"
              />
            )}
          </button>
        </div>
      </div>

      <div className="flex w-full flex-row items-center justify-between pt-0.5 pb-3">
        <div className="flex flex-col items-start justify-start space-y-1 w-full">
          <div className="w-full">
            <h3 className="text-[15px] w-full font-[500] tracking-tight text-neutral-800">
              {truncateTitle(listing.listing_name, 32)}
            </h3>
          </div>
          <div className="w-full flex flex-row items-center justify-start space-x-2 pr-1">
            <div className="flex flex-row items-center space-x-1.5 w-full pt-1">
              <div className="py-1 bg-neutral-100 flex flex-row items-center space-x-1.5 px-2 text-neutral-600 rounded-md">
                <Bed
                  className="h-3.5 w-3.5 text-neutral-700"
                  strokeWidth={1.5}
                />
                <p className="text-[13px] font-light">
                  {listing.bedroom_count}
                </p>
              </div>
              <div className="py-1 bg-neutral-100 flex flex-row items-center space-x-1.5 px-2 text-neutral-600 rounded-md">
                <Users
                  className="h-3.5 w-3.5 text-neutral-700"
                  strokeWidth={1.5}
                />
                <p className="text-[13px] font-light">{listing.guest_count}</p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={() => handleAddToTrip(listing)}
                className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-indigo-800 hover:bg-indigo-700 font-medium transition-all duration-500 ease-in-out text-white"
              >
                {addingLodging.isAdding &&
                addingLodging.lodgingId === listing.listingid ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <i className="fa-solid fa-plus text-xs"></i>
                )}
              </button>
            )}
            <button
              onClick={() => handleModalOpenMainLodging(listing)}
              className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 font-medium transition-all duration-500 ease-in-out text-neutral-800"
            >
              <p className="text-xs font-bold italic">i</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const truncateTitle = (title: string, maxLength: number) => {
  if (title.length > maxLength) {
    return title.slice(0, maxLength) + "...";
  }
  return title;
};

export default AccommodationCard;
